import { create } from 'zustand'
import { supabase } from '@/lib/supabase'
import type { Session, User } from '@supabase/supabase-js'
import type { AccountInfo } from '@azure/msal-browser'
import type { DemoProfile } from '@/types/demoProfiles'
import { queryClient } from '@/lib/queryClient'
import { isSelfSignupAllowed, ssoDomain } from '@/config/authConfig'
import { isEntraAuthEnabled, entraGetAccount, entraSignIn, entraSignOut, entraSystemRole } from '@/lib/entraAuth'

/**
 * Session/User shim for Entra sign-ins (enterprise build): presents the MSAL
 * account in the Supabase Session shape the rest of the app consumes.
 * user.id = the Entra oid, which the enterprise roster (public.users) keys on.
 * The casts are confined to this one builder.
 */
function entraSessionShim(account: AccountInfo, systemRole: string): Session {
  const user = {
    id: account.localAccountId,
    email: account.username,
    user_metadata: { full_name: account.name ?? account.username, system_role: systemRole },
    app_metadata: { provider: 'entra' },
    aud: 'entra',
    created_at: '',
  } as unknown as User
  return { access_token: '', refresh_token: '', expires_in: 0, token_type: 'bearer', user } as unknown as Session
}

interface AuthState {
  session: Session | null
  user: User | null
  loading: boolean
  error: string | null

  initialize: () => Promise<void>
  signIn: (email: string, password: string) => Promise<void>
  /** Redirect to the fork's IdP for SAML SSO (Supabase-native). Product builds only. */
  signInWithSso: (domain?: string) => Promise<void>
  /** MSAL popup sign-in against Entra ID. Enterprise (Azure) builds only. */
  signInWithEntra: () => Promise<void>
  signUp: (email: string, password: string, fullName: string, organizationName?: string) => Promise<void>
  signOut: () => Promise<void>
  /** Sign out an authenticated-but-unprovisioned user and show a clear reason.
   *  Used by the SSO JIT 'require_roster' policy: a valid login with no roster row. */
  denyAccess: (message: string) => Promise<void>
  clearError: () => void
  /** Auto-sign-in as the demo user for a given profile */
  signInAsDemo: (profile: DemoProfile) => Promise<void>
}

export const useAuthStore = create<AuthState>((set) => ({
  session: null,
  user: null,
  loading: true,
  error: null,

  initialize: async () => {
    if (isEntraAuthEnabled()) {
      // Enterprise build: restore the MSAL account (sessionStorage cache).
      try {
        const account = await entraGetAccount()
        if (account) {
          const session = entraSessionShim(account, await entraSystemRole())
          set({ session, user: session.user, loading: false })
        } else {
          set({ loading: false })
        }
      } catch {
        set({ loading: false })
      }
      return
    }
    try {
      const { data: { session } } = await supabase.auth.getSession()
      set({ session, user: session?.user ?? null, loading: false })

      // Listen for auth state changes (token refresh, sign-out from another tab, etc.)
      supabase.auth.onAuthStateChange((_event, session) => {
        set({ session, user: session?.user ?? null })
      })
    } catch {
      set({ loading: false })
    }
  },

  signInWithEntra: async () => {
    set({ error: null, loading: true })
    try {
      // Full-page redirect: never resolves. The session is established by
      // initialize() when the redirect returns (handleRedirectPromise).
      await entraSignIn()
    } catch (e) {
      set({ error: e instanceof Error ? e.message : 'Microsoft sign-in failed', loading: false })
    }
  },

  signIn: async (email, password) => {
    set({ error: null, loading: true })
    const { error } = await supabase.auth.signInWithPassword({ email, password })
    if (error) {
      set({ error: error.message, loading: false })
    } else {
      set({ loading: false })
    }
  },

  signInWithSso: async (domain) => {
    const d = domain ?? ssoDomain()
    if (!d) {
      set({ error: 'No SSO domain is configured for this deployment.', loading: false })
      return
    }
    set({ error: null, loading: true })
    // Supabase-native SAML SSO: returns the IdP redirect URL; the session lands
    // back via the auth callback + onAuthStateChange (initialize()'s listener).
    const { data, error } = await supabase.auth.signInWithSSO({ domain: d })
    if (error) {
      set({ error: error.message, loading: false })
      return
    }
    if (data?.url) {
      window.location.href = data.url
    } else {
      set({ error: 'SSO provider did not return a sign-in URL.', loading: false })
    }
  },

  signUp: async (email, password, fullName, organizationName) => {
    // Product/client builds have no self-service signup (locked model): users
    // arrive via SSO JIT or admin/org import. Refuse before touching Supabase.
    // (Demo auto-sign-in uses supabase.auth.signUp directly, not this method, so
    // it is unaffected; on internal/demo isSelfSignupAllowed() is true anyway.)
    if (!isSelfSignupAllowed()) {
      set({ error: 'Self-service signup is disabled on this deployment. Contact your administrator for access.', loading: false })
      return
    }
    set({ error: null, loading: true })
    // Generate a stable org ID from the organization name
    const orgId = organizationName
      ? `org_${organizationName.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, '')}`
      : null
    const { error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: {
          full_name: fullName,
          organization_id: orgId,
          organization_name: organizationName || null,
        },
      },
    })
    if (error) {
      set({ error: error.message, loading: false })
    } else {
      set({ error: null, loading: false })
    }
  },

  signOut: async () => {
    if (isEntraAuthEnabled()) {
      await entraSignOut()
    } else {
      await supabase.auth.signOut()
    }
    set({ session: null, user: null })
    // Drop all cached server state on sign-out so the next user (e.g. a fast
    // re-login on a shared machine) can't briefly see the previous user's
    // portfolios/projects/etc. from React Query's cache.
    queryClient.clear()
  },

  denyAccess: async (message) => {
    // Valid credentials, but no roster row on a locked (product) build. Tear the
    // session down so no partial/metadata identity leaks in, and land the user
    // back on the wall with the reason. Mirrors signOut's cache clear.
    await supabase.auth.signOut()
    set({ session: null, user: null, error: message, loading: false })
    queryClient.clear()
  },

  clearError: () => set({ error: null }),

  signInAsDemo: async (profile) => {
    if (!profile.demoAuth) {
      // No auth swap for this profile — still refetch for the new profile's
      // data (setDemoProfile delegates its single invalidate wave to here).
      void queryClient.invalidateQueries()
      return
    }
    const { email, password } = profile.demoAuth

    // Try sign-in first; if user doesn't exist yet, auto-create
    const { error } = await supabase.auth.signInWithPassword({ email, password })
    if (error?.message?.includes('Invalid login credentials')) {
      // User doesn't exist — sign up, then sign in
      const { error: signUpError } = await supabase.auth.signUp({
        email,
        password,
        options: { data: { full_name: `Demo User (${profile.name})` } },
      })
      if (signUpError) {
        console.warn('[auth] Demo user signup failed:', signUpError.message)
        return
      }
      // Sign in after signup
      await supabase.auth.signInWithPassword({ email, password })
    } else if (error) {
      console.warn('[auth] Demo sign-in failed:', error.message)
    }

    // Re-fetch all data now that we have an authenticated session
    queryClient.invalidateQueries()
  },
}))
